// Client-side HEIC/HEIF → JPEG conversion. iPhone uploads frequently arrive
// with Content-Type "image/jpeg" and a ".jpeg" extension even though the
// bytes are HEIC (the default iPhone camera format). Browsers can't render
// HEIC in <img>, our server-side image pipeline (sharp) can't decode it in
// this environment, and the storage finalize route rejects ISO BMFF bytes,
// so the only way uploads "just work" for iPhone users is to convert
// client-side BEFORE creating a preview URL or uploading.

const HEIC_BRANDS = new Set([
  "heic",
  "heix",
  "hevc",
  "hevx",
  "heim",
  "heis",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
]);

/**
 * Detects HEIC/HEIF by inspecting the file's first 32 bytes for an ISO BMFF
 * "ftyp" box with a HEIC-family brand in either the major-brand slot OR the
 * compatible-brands list. Extension/MIME alone are not reliable because
 * iPhone often labels HEIC files as image/jpeg with a .jpeg extension, and
 * some HEIF derivatives use a non-HEIC major brand but list a HEIC brand as
 * compatible.
 */
export async function isHeicFile(file: File): Promise<boolean> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".heic") || name.endsWith(".heif")) return true;
  if (file.size < 16) return false;
  try {
    const readLen = Math.min(32, file.size);
    const head = new Uint8Array(await file.slice(0, readLen).arrayBuffer());
    if (head[4] !== 0x66 || head[5] !== 0x74 || head[6] !== 0x79 || head[7] !== 0x70) {
      return false;
    }
    // Major brand at bytes 8..11, minor version at 12..15, then a list of
    // 4-byte compatible brands until the end of the ftyp box (box size in
    // bytes 0..3, big-endian). We scan up to whatever we read (max 32 bytes
    // → up to 4 compatible brand slots, plenty for HEIC detection).
    const boxSize = (head[0] << 24) | (head[1] << 16) | (head[2] << 8) | head[3];
    const end = Math.min(readLen, boxSize > 0 ? boxSize : readLen);
    // Check major brand
    const major = String.fromCharCode(head[8], head[9], head[10], head[11]);
    if (HEIC_BRANDS.has(major)) return true;
    // Check compatible brands starting at byte 16
    for (let offset = 16; offset + 4 <= end; offset += 4) {
      const brand = String.fromCharCode(head[offset], head[offset + 1], head[offset + 2], head[offset + 3]);
      if (HEIC_BRANDS.has(brand)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Converts a HEIC/HEIF file to a JPEG File. The conversion library is
 * dynamically imported so its ~1MB libheif WASM bundle stays out of the
 * main app chunk and only loads when an iPhone user actually drops a HEIC
 * file. Throws a descriptive Error on failure.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  let heicTo: typeof import("heic-to").heicTo;
  try {
    ({ heicTo } = await import("heic-to"));
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`Couldn't load HEIC converter: ${detail}`);
  }
  const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.92 });
  const newName = file.name.replace(/\.(heic|heif|jpe?g)$/i, "") + ".jpg";
  return new File([blob], newName, { type: "image/jpeg", lastModified: file.lastModified });
}

/**
 * If the given file is HEIC/HEIF, converts it to JPEG. Otherwise returns the
 * original file. Useful at any "user picked a file" boundary (drop, paste,
 * file input, upload hook) to guarantee downstream code only ever sees a
 * format that browsers and our server pipeline can handle.
 */
export async function normaliseToWebSafeImage(file: File): Promise<File> {
  if (await isHeicFile(file)) {
    return convertHeicToJpeg(file);
  }
  return file;
}
